import { BaseAPIRequestFactory, RequiredError } from './baseapi';
import type { Configuration } from '../configuration';
import { RequestContext, HttpMethod, ResponseContext, HttpInfo } from '../http/http';
import { ObjectSerializer } from '../models/ObjectSerializer';
import { ApiException } from './exception';
import { isCodeInRange } from '../util';
import type {SecurityAuthentication} from '../auth/auth';
import type {HttpFile} from '../http/http';


import { CreateSessionRequest } from '../models/CreateSessionRequest';
import { SearchResources200Response } from '../models/SearchResources200Response';
import { SearchResourcesRequest } from '../models/SearchResourcesRequest';
import { SendMessageToAI400Response } from '../models/SendMessageToAI400Response';
import { SendMessageToAIRequest } from '../models/SendMessageToAIRequest';
import { Session } from '../models/Session';
import { UpdateSessionRequest } from '../models/UpdateSessionRequest';

/**
 * no description
 */
export class AiAgentApiRequestFactory extends BaseAPIRequestFactory {

  /**
   * Create AI Agent session
   * @param projectId The project ID
   * @param createSessionRequest 
   */
  public async createAIAgentSession(projectId: string, createSessionRequest?: CreateSessionRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("AiAgentApi", "createAIAgentSession", "projectId");
    }



    // Path Params
    const localVarPath = '/projects/{projectId}/ai-agent/sessions'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(createSessionRequest, "CreateSessionRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Delete AI Agent session
   * @param projectId The project ID
   * @param sessionId The session ID
   */
  public async deleteAIAgentSession(projectId: string, sessionId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("AiAgentApi", "deleteAIAgentSession", "projectId");
    }


    // verify required parameter 'sessionId' is not null or undefined
    if (sessionId === null || sessionId === undefined) {
      throw new RequiredError("AiAgentApi", "deleteAIAgentSession", "sessionId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/ai-agent/sessions/{sessionId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'sessionId' + '}', encodeURIComponent(String(sessionId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get AI Agent session
   * @param projectId The project ID
   * @param sessionId The session ID
   */
  public async getAIAgentSession(projectId: string, sessionId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("AiAgentApi", "getAIAgentSession", "projectId");
    }


    // verify required parameter 'sessionId' is not null or undefined
    if (sessionId === null || sessionId === undefined) {
      throw new RequiredError("AiAgentApi", "getAIAgentSession", "sessionId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/ai-agent/sessions/{sessionId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'sessionId' + '}', encodeURIComponent(String(sessionId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List AI Agent sessions
   * @param projectId The project ID
   */
  public async listAIAgentSessions(projectId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("AiAgentApi", "listAIAgentSessions", "projectId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/ai-agent/sessions'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Search resources using semantic and keyword search
   * @param projectId The project ID
   * @param searchResourcesRequest 
   */
  public async searchResources(projectId: string, searchResourcesRequest: SearchResourcesRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("AiAgentApi", "searchResources", "projectId");
    }


    // verify required parameter 'searchResourcesRequest' is not null or undefined
    if (searchResourcesRequest === null || searchResourcesRequest === undefined) {
      throw new RequiredError("AiAgentApi", "searchResources", "searchResourcesRequest");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/semantic-search'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(searchResourcesRequest, "SearchResourcesRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Send message to AI Agent
   * @param projectId The project ID
   * @param sendMessageToAIRequest 
   */
  public async sendMessageToAI(projectId: string, sendMessageToAIRequest: SendMessageToAIRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("AiAgentApi", "sendMessageToAI", "projectId");
    }


    // verify required parameter 'sendMessageToAIRequest' is not null or undefined
    if (sendMessageToAIRequest === null || sendMessageToAIRequest === undefined) {
      throw new RequiredError("AiAgentApi", "sendMessageToAI", "sendMessageToAIRequest");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/ai-agent/chat'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(sendMessageToAIRequest, "SendMessageToAIRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Update AI Agent session
   * @param projectId The project ID
   * @param sessionId The session ID
   * @param updateSessionRequest 
   */
  public async updateAIAgentSession(projectId: string, sessionId: string, updateSessionRequest: UpdateSessionRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("AiAgentApi", "updateAIAgentSession", "projectId");
    }


    // verify required parameter 'sessionId' is not null or undefined
    if (sessionId === null || sessionId === undefined) {
      throw new RequiredError("AiAgentApi", "updateAIAgentSession", "sessionId");
    }


    // verify required parameter 'updateSessionRequest' is not null or undefined
    if (updateSessionRequest === null || updateSessionRequest === undefined) {
      throw new RequiredError("AiAgentApi", "updateAIAgentSession", "updateSessionRequest");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/ai-agent/sessions/{sessionId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'sessionId' + '}', encodeURIComponent(String(sessionId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.PATCH, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(updateSessionRequest, "UpdateSessionRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

}

export class AiAgentApiResponseProcessor {

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to createAIAgentSession
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async createAIAgentSessionWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Session >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: Session = ObjectSerializer.deserialize(
        await response.body.json(),
        "Session", ""
      ) as Session;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Invalid request", body, response.headers, response);
    }
    if (isCodeInRange("502", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Failed to reach AI agent service", body, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Session = await response.body.json() as Session;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to deleteAIAgentSession
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async deleteAIAgentSessionWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("204", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("502", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Failed to reach AI agent service", body, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getAIAgentSession
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getAIAgentSessionWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Session >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: Session = ObjectSerializer.deserialize(
        await response.body.json(),
        "Session", ""
      ) as Session;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Session not found", body, response.headers, response);
    }
    if (isCodeInRange("502", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Failed to reach AI agent service", body, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Session = await response.body.json() as Session;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listAIAgentSessions
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listAIAgentSessionsWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Array<Session> >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: Array<Session> = ObjectSerializer.deserialize(
        await response.body.json(),
        "Array<Session>", ""
      ) as Array<Session>;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("502", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Failed to reach AI agent service", body, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Array<Session> = await response.body.json() as Array<Session>;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to searchResources
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async searchResourcesWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SearchResources200Response >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SearchResources200Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SearchResources200Response", ""
      ) as SearchResources200Response;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Invalid request", body, response.headers, response);
    }
    if (isCodeInRange("502", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Failed to reach AI agent service", body, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SearchResources200Response = await response.body.json() as SearchResources200Response;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to sendMessageToAI
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async sendMessageToAIWithHttpInfo(response: ResponseContext): Promise<HttpInfo<string >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: string = ObjectSerializer.deserialize(
        await response.body.json(),
        "string", ""
      ) as string;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Invalid request", body, response.headers, response);
    }
    if (isCodeInRange("502", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Failed to reach AI agent service", body, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: string = await response.body.json() as string;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to updateAIAgentSession
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async updateAIAgentSessionWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Session >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: Session = ObjectSerializer.deserialize(
        await response.body.json(),
        "Session", ""
      ) as Session;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Session not found", body, response.headers, response);
    }
    if (isCodeInRange("502", response.httpStatusCode)) {
      const body: SendMessageToAI400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "SendMessageToAI400Response", ""
      ) as SendMessageToAI400Response;
      throw new ApiException<SendMessageToAI400Response>(response.httpStatusCode, "Failed to reach AI agent service", body, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Session = await response.body.json() as Session;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

}
